/* eslint-disable no-unused-vars */
import { useState } from "react";
import { formatNumberWithCommas } from "@/lib/utilities";

// LQC-2494 - how many contracts a future ticket buys or sells. Deliberately NOT the
// bond NominalInput: the M / MM shorthand belongs to face values, and a contract count
// is a small whole number, so "10m" here would be a typo rather than ten million.
//
// While focused the raw digits are shown so they can be edited; blurred it shows the
// count with thousand separators.

interface IContractCountInput {
  value: number | null;
  onCommit: (value: number | null) => void;
  testId: string;
}

// Whole numbers only, and an empty string while the field is being cleared.
export function isValidContractCountInput(value: string): boolean {
  return /^\d*$/.test(value);
}

export function returnCommittedContractCount(value: string): number | null {
  const trimmed = value.trim();
  if (trimmed === "") return null;
  const parsed = Number(trimmed);
  return isNaN(parsed) || parsed <= 0 ? null : Math.floor(parsed);
}

const ContractCountInput = ({
  value,
  onCommit,
  testId,
}: IContractCountInput) => {
  const [isEditing, setIsEditing] = useState(false);
  const [localValue, setLocalValue] = useState("");

  const displayValue = isEditing
    ? localValue
    : value !== null
      ? formatNumberWithCommas(value)
      : "";

  return (
    <div className="flex h-[28px] w-full items-center rounded-md border border-gray-300 dark:border-gray-600 dark:bg-gray-800">
      <input
        data-testid={testId}
        id={testId}
        type="text"
        inputMode="numeric"
        placeholder="e.g. 25"
        className="h-full w-full bg-transparent px-2 text-xs outline-none dark:text-gray-200"
        value={displayValue}
        onFocus={() => {
          setLocalValue(value !== null ? String(value) : "");
          setIsEditing(true);
        }}
        onChange={(e) => {
          if (isValidContractCountInput(e.target.value)) {
            setLocalValue(e.target.value);
          }
        }}
        onBlur={() => {
          setIsEditing(false);
          onCommit(returnCommittedContractCount(localValue));
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") e.currentTarget.blur();
        }}
        onCopy={(e) => {
          if (value === null) return;
          e.preventDefault();
          e.clipboardData.setData("text/plain", String(value));
        }}
      />
    </div>
  );
};

export default ContractCountInput;
